<template>
  <b-container>
    <config-publisher
      ref="configPublisher"
      v-model="config"
      class="p-0"
      tool-name="Banner Creator"
      :config-name="configName"
      :can-edit="canBannersEdit"
      :can-publish="canBannersPublish">
      <template slot="header">
        <b-col class="pr-0">
          <b-form-radio-group
            v-model="configName"
            class="float-right"
            buttons
            button-variant="outline-primary">
            <b-form-radio value="banners_en">
              EN
            </b-form-radio>
            <b-form-radio value="banners_es">
              ES
            </b-form-radio>
            <b-form-radio value="banners_ja">
              JA
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
      </template>
      <template v-slot:default="{editable}">
        <b-card>
          <b-row>
            <b-form-radio-group
              v-model="platform"
              buttons
              button-variant="outline-primary"
              class="ml-3"
              :disabled="!configIsLoaded()">
              <b-form-radio
                v-model="platform"
                value="ios">
                <i class="fab fa-apple" />
              </b-form-radio>
              <b-form-radio
                v-model="platform"
                value="android">
                <i class="fab fa-android" />
              </b-form-radio>
              <b-form-radio
                v-model="platform"
                value="unfiltered">
                <i class="fas fa-times" />
              </b-form-radio>
            </b-form-radio-group>
            <b-form-radio-group
              v-model="device"
              buttons
              button-variant="outline-primary"
              class="RadioGroup ml-3"
              :disabled="!configIsLoaded()">
              <b-form-radio
                v-model="device"
                value="phone">
                <i class="fas fa-mobile-alt" />
              </b-form-radio>
              <b-form-radio
                v-model="device"
                value="tablet">
                <i class="fas fa-tablet" />
              </b-form-radio>
              <b-form-radio
                v-model="device"
                value="unfiltered">
                <i class="fas fa-times" />
              </b-form-radio>
            </b-form-radio-group>
          </b-row>
          <hr>
          <b-list-group>
            <draggable
              v-model="config.banners"
              tag="ul"
              class="list-group mt-3"
              handle=".drag-title">
              <template v-for="(banner, idx) in config.banners">
                <b-list-group-item
                  v-if="showBanner(banner)"
                  :key="banner.id">
                  <div
                    v-if="editable && canBannersEdit"
                    class="drag-title">
                    <span
                      v-b-toggle="banner.id"
                      class="pointer">
                      <i class="fa fa-flag" />
                      {{ banner.title }}
                    </span>
                    <i
                      class="fa fa-times close"
                      @click="removeBanner(banner)" />
                  </div>
                  <div v-else>
                    <span
                      v-b-toggle="banner.id"
                      class="pointer">
                      <i class="fa fa-flag" />
                      {{ banner.title }}
                    </span>
                  </div>
                  <b-collapse :id="banner.id">
                    <div class="mt-3">
                      <Banner
                        v-model="config.banners[idx]"
                        :editable="editable && canBannersEdit"
                        class="mt-3" />
                    </div>
                  </b-collapse>
                </b-list-group-item>
              </template>
            </draggable>
          </b-list-group>
          <b-row align-h="end">
            <b-col
              class="mt-3 mb-3"
              cols="auto">
              <b-button
                variant="outline-primary"
                :disabled="!editable || !canBannersEdit"
                @click="addBanner">
                Add Banner
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </template>
    </config-publisher>
  </b-container>
</template>

<script>
  import Banner from '@/components/Banner'
  import draggable from 'vuedraggable'
  import { mapGetters } from 'vuex'
  import ConfirmRemoval from '@/mixins/ConfirmRemoval'
  import ConfigPublisher from "@/components/ConfigPublisher"
  import uuid from "uuid"

  export default {
    name: 'Banners',
    components: {
      ConfigPublisher,
      Banner,
      draggable,
    },
    mixins: [ConfirmRemoval],
    data: () => ({
      config: {
        banners: [],
      },
      configName: "banners_en",
      platform: "unfiltered",
      device: "unfiltered",
    }),
    computed: {
      ...mapGetters([
        'betaEnvName',
        'liveEnvName',
        'canBannersEdit',
        'canBannersPublish',
      ]),
    },
    async beforeRouteLeave(to, from, next) {
      if (this.$refs.configPublisher.configHasChanged()) {
        const message = `You have unpublished changes to your config, which will be lost.\n\nClick "Cancel" to go back and publish your work, or click "Yes" to lose your changes.`
        next(await this.$confirm.open(message))
      } else {
        next()
      }
    },
    methods: {
      configIsLoaded() {
        return this.config && this.config.banners && this.config.banners.length > 0
      },
      removeBanner(banner) {
        this.confirmRemoval({ id: banner.title }, () => {
          this.config.banners = this.config.banners.filter(b => b.id !== banner.id)
        })
      },
      showBanner(banner) {
        const found = this.config.banners.find(b => b.id === banner.id)
        let show = true
        if (this.platform.includes("ios") && !found.ios) {
          show = false
        }
        if (this.platform.includes("android") && !found.android) {
          show = false
        }
        if (this.device.includes("phone") && !found.phone) {
          show = false
        }
        if (this.device.includes("tablet") && !found.tablet) {
          show = false
        }
        return show
      },
      addBanner() {
        const id = uuid.v4()
        this.config.banners.push({
          id,
          ios: true,
          android: true,
          paid: true,
          lite: true,
          phone: true,
          tablet: true,
          appVersion: '',
          osVersion: '',
          entitlement: '',
          accessFeature: '',
          screen: '',
          viewid: '',
          type: '',
          webviewtype: '4',
          title: id,
          image: '',
          imageTabletLandscape: '',
          imageTabletPortrait: '',
          closable: true,
          link: '',
          backgroundColor: '',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
          trackingString: '',
          adMarvelId: '',
          adMarvelMLBTarget: '',
          adMarvelWidth: '',
          adMarvelHeight: '',
          adMarvelSectionId: '',
          admarvelExpiryTimeInSec: '',
          contentID: '',
          calendarEventID: '',
          requiresAuthentication: false,
          sponsor: '',
          animate: false,
        })
      },
    },
  }
</script>

<style scoped>
.checkbox-group {
  width: 10rem;
}
</style>
